// src/pages/admin/AdminTiendasRecomendadas.js
//
// Tiendas recomendadas por vecis — "Por llegar" (D-22).
//
// El SuperAdmin ve todos los prospectos ordenados por votos, qué guías
// cubren cada pin con su radio, y los que no tienen cobertura. Puede:
//   · Asignar a un guía (aunque el pin quede fuera de su radio).
//   · Retirar (contenido indebido, duplicada, no existe).
//   · Reabrir (vuelve a Por llegar sin guía).
//   · Marcar No le interesa.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, FormControl, InputLabel, Select, MenuItem, Stack, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel,
  Checkbox, Autocomplete, CircularProgress, Tooltip, Link,
} from '@mui/material';
import RefreshIcon       from '@mui/icons-material/Refresh';
import MapIcon           from '@mui/icons-material/Map';
import PersonAddIcon     from '@mui/icons-material/PersonAdd';
import BlockIcon         from '@mui/icons-material/Block';
import ReplayIcon        from '@mui/icons-material/Replay';
import DoNotDisturbIcon  from '@mui/icons-material/DoNotDisturb';
import { tiendasRecomendadas } from '../../api/tiendasRecomendadasApi';
import { validacion } from '../../api/authApi';
import { fechaHoraLocal } from '../../utils/format';

const ESTADOS = [
  { value: '',            label: 'Todos' },
  { value: 'POR_LLEGAR',  label: 'Por llegar' },
  { value: 'TOMADA',      label: 'Tomada por guía' },
  { value: 'VISITADA',    label: 'Visitada' },
  { value: 'AFILIADA',    label: 'Afiliada' },
  { value: 'NO_INTERESA', label: 'No le interesa' },
  { value: 'RETIRADA',    label: 'Retirada' },
];

const etiquetaEstado = (e) => ESTADOS.find((x) => x.value === e)?.label || e;

const colorEstado = (e) => ({
  POR_LLEGAR: 'warning',
  TOMADA: 'info',
  VISITADA: 'info',
  AFILIADA: 'success',
  NO_INTERESA: 'default',
  RETIRADA: 'error',
}[e] || 'default');

const mensajeError = (e, fallback) => e?.mensajeUsuario || e?.message || fallback;

export default function AdminTiendasRecomendadas() {
  const [estado, setEstado]                 = useState('');
  const [soloSinCobertura, setSoloSinCob]   = useState(false);
  const [texto, setTexto]                   = useState('');
  const [textoAplicado, setTextoAplicado]   = useState('');
  const [items, setItems]                   = useState([]);
  const [resumen, setResumen]               = useState([]);
  const [cargando, setCargando]             = useState(false);
  const [error, setError]                   = useState(null);

  // Diálogo asignar guía
  const [asignando, setAsignando]           = useState(null);
  const [guiasOpciones, setGuiasOpciones]   = useState([]);
  const [guiaBusqueda, setGuiaBusqueda]     = useState('');
  const [guiaElegido, setGuiaElegido]       = useState(null);
  const [cargandoGuias, setCargandoGuias]   = useState(false);

  // Diálogo cambio de estado
  const [cambioEstado, setCambioEstado]     = useState(null); // { item, estado }
  const [notas, setNotas]                   = useState('');
  const [guardando, setGuardando]           = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await tiendasRecomendadas.listar({
        estado: estado || undefined,
        soloSinCobertura,
        texto: textoAplicado || undefined,
      });
      setItems(r.items || []);
      setResumen(r.resumen || []);
    } catch (e) {
      setError(mensajeError(e, 'No se pudo cargar la lista.'));
    } finally {
      setCargando(false);
    }
  }, [estado, soloSinCobertura, textoAplicado]);

  useEffect(() => { cargar(); }, [cargar]);

  // Búsqueda de guías con debounce para el Autocomplete
  useEffect(() => {
    if (!asignando) return undefined;
    let cancelado = false;
    const t = setTimeout(async () => {
      setCargandoGuias(true);
      try {
        const r = await validacion.guiasDisponibles(guiaBusqueda || undefined);
        if (!cancelado) setGuiasOpciones(r.guias || []);
      } catch {
        if (!cancelado) setGuiasOpciones([]);
      } finally {
        if (!cancelado) setCargandoGuias(false);
      }
    }, 300);
    return () => { cancelado = true; clearTimeout(t); };
  }, [asignando, guiaBusqueda]);

  const abrirAsignar = (item) => {
    setAsignando(item);
    setGuiaElegido(null);
    setGuiaBusqueda('');
  };

  const confirmarAsignar = async () => {
    if (!guiaElegido) return;
    setGuardando(true);
    try {
      await tiendasRecomendadas.asignar(asignando.tiendaRecomendadaId, guiaElegido.guiaId);
      setAsignando(null);
      cargar();
    } catch (e) {
      alert(mensajeError(e, 'No se pudo asignar.'));
    } finally {
      setGuardando(false);
    }
  };

  const abrirCambioEstado = (item, nuevoEstado) => {
    setCambioEstado({ item, estado: nuevoEstado });
    setNotas('');
  };

  const confirmarCambioEstado = async () => {
    const requiereNotas = cambioEstado.estado !== 'POR_LLEGAR';
    if (requiereNotas && !notas.trim()) return;
    setGuardando(true);
    try {
      await tiendasRecomendadas.cambiarEstado(
        cambioEstado.item.tiendaRecomendadaId, cambioEstado.estado, notas.trim() || null);
      setCambioEstado(null);
      cargar();
    } catch (e) {
      alert(mensajeError(e, 'No se pudo cambiar el estado.'));
    } finally {
      setGuardando(false);
    }
  };

  const totalEstado = (e) => resumen.find((r) => r.estado === e)?.total || 0;
  const vivos = ['POR_LLEGAR', 'TOMADA', 'VISITADA'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Tiendas recomendadas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tiendas que los vecis quieren en HolaVeci. Las que no están en el radio de ningún guía
        activo aparecen como <b>sin cobertura</b> y se deben asignar manualmente.
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {ESTADOS.filter((e) => e.value).map((e) => (
          <Chip
            key={e.value}
            color={colorEstado(e.value)}
            variant={estado === e.value ? 'filled' : 'outlined'}
            label={`${e.label}: ${totalEstado(e.value)}`}
            onClick={() => setEstado(estado === e.value ? '' : e.value)}
          />
        ))}
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
              {ESTADOS.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Buscar por nombre"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setTextoAplicado(texto.trim()); }}
            onBlur={() => setTextoAplicado(texto.trim())}
          />
          <FormControlLabel
            control={<Checkbox checked={soloSinCobertura} onChange={(e) => setSoloSinCob(e.target.checked)} />}
            label="Solo sin cobertura"
          />
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<RefreshIcon />} onClick={cargar} disabled={cargando}>
            Actualizar
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tienda</TableCell>
              <TableCell align="center">Votos</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Guía</TableCell>
              <TableCell>Cobertura</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Creada</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!cargando && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No hay tiendas recomendadas con estos filtros.
                </TableCell>
              </TableRow>
            )}
            {!cargando && items.map((t) => (
              <TableRow key={t.tiendaRecomendadaId} hover>
                <TableCell sx={{ minWidth: 240 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {t.fotoUrl ? (
                      <Link href={t.fotoUrl} target="_blank" rel="noopener noreferrer">
                        <Box component="img" src={t.fotoUrl} alt=""
                          sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }} />
                      </Link>
                    ) : (
                      <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'grey.100',
                                 display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                        {t.categoria?.emoji || '🏪'}
                      </Box>
                    )}
                    <Box>
                      <Typography fontWeight={700}>{t.nombre}</Typography>
                      {t.categoria && (
                        <Typography variant="caption" color="text.secondary">{t.categoria.nombre}</Typography>
                      )}
                      {t.comentario && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', maxWidth: 280 }}>
                          “{t.comentario}”
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={800}>{t.votos}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={colorEstado(t.estado)} label={etiquetaEstado(t.estado)} />
                  {t.notasGuia && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, maxWidth: 200 }}>
                      Guía: {t.notasGuia}
                    </Typography>
                  )}
                  {t.notasAdmin && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ maxWidth: 200 }}>
                      Admin: {t.notasAdmin}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {t.guia ? (
                    <>
                      <div><b>{t.guia.nombreCompleto}</b></div>
                      {t.guia.telefono && <div style={{ fontSize: 12 }}>{t.guia.telefono}</div>}
                      {t.asignadaPorAdmin && <Chip size="small" label="Asignada por admin" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />}
                    </>
                  ) : <span style={{ color: '#999' }}>—</span>}
                </TableCell>
                <TableCell>
                  {t.sinCobertura ? (
                    <Chip size="small" color="error" variant="outlined" label="Sin cobertura" />
                  ) : (
                    <Tooltip title={t.guiasQueCubren.map((g) => g.nombreCompleto).join(', ')}>
                      <Chip size="small" variant="outlined"
                        label={`${t.guiasQueCubren.length} guía${t.guiasQueCubren.length === 1 ? '' : 's'}`} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {t.telefono || <span style={{ color: '#999' }}>—</span>}
                  {t.creadaPor && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Recomendó: {t.creadaPor}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fechaHoraLocal(t.fechaCreacion)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Ver en Google Maps">
                    <Button size="small"
                      href={`https://www.google.com/maps/search/?api=1&query=${t.latitud},${t.longitud}`}
                      target="_blank" rel="noopener noreferrer">
                      <MapIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                  {vivos.includes(t.estado) && (
                    <Tooltip title="Asignar a guía">
                      <Button size="small" onClick={() => abrirAsignar(t)}><PersonAddIcon fontSize="small" /></Button>
                    </Tooltip>
                  )}
                  {t.estado !== 'AFILIADA' && t.estado !== 'POR_LLEGAR' && (
                    <Tooltip title="Reabrir (Por llegar, sin guía)">
                      <Button size="small" onClick={() => abrirCambioEstado(t, 'POR_LLEGAR')}><ReplayIcon fontSize="small" /></Button>
                    </Tooltip>
                  )}
                  {vivos.includes(t.estado) && (
                    <Tooltip title="No le interesa">
                      <Button size="small" onClick={() => abrirCambioEstado(t, 'NO_INTERESA')}><DoNotDisturbIcon fontSize="small" /></Button>
                    </Tooltip>
                  )}
                  {t.estado !== 'AFILIADA' && t.estado !== 'RETIRADA' && (
                    <Tooltip title="Retirar">
                      <Button size="small" color="error" onClick={() => abrirCambioEstado(t, 'RETIRADA')}><BlockIcon fontSize="small" /></Button>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Asignar guía */}
      <Dialog open={!!asignando} onClose={() => !guardando && setAsignando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Asignar “{asignando?.nombre}” a un guía</DialogTitle>
        <DialogContent>
          {asignando && !asignando.sinCobertura && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Guías que cubren este punto: {asignando.guiasQueCubren.map((g) => g.nombreCompleto).join(', ')}
            </Alert>
          )}
          {asignando?.sinCobertura && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ningún guía activo tiene este punto dentro de su radio. El guía que elijas la verá en “Mías”.
            </Alert>
          )}
          <Autocomplete
            options={guiasOpciones}
            loading={cargandoGuias}
            value={guiaElegido}
            onChange={(_, v) => setGuiaElegido(v)}
            onInputChange={(_, v) => setGuiaBusqueda(v)}
            filterOptions={(x) => x}
            isOptionEqualToValue={(a, b) => a.guiaId === b.guiaId}
            getOptionLabel={(g) => `${g.nombreCompleto} · ${g.numeroDocumento}${g.codigoInvitacion ? ` · ${g.codigoInvitacion}` : ''}`}
            renderInput={(params) => (
              <TextField {...params} label="Buscar guía por nombre, documento o código" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsignando(null)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarAsignar} disabled={!guiaElegido || guardando}>
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cambio de estado */}
      <Dialog open={!!cambioEstado} onClose={() => !guardando && setCambioEstado(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {cambioEstado?.estado === 'RETIRADA' && 'Retirar tienda'}
          {cambioEstado?.estado === 'POR_LLEGAR' && 'Reabrir tienda'}
          {cambioEstado?.estado === 'NO_INTERESA' && 'Marcar como No le interesa'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <b>{cambioEstado?.item?.nombre}</b>
            {cambioEstado?.estado === 'RETIRADA' && ' saldrá de la vista de vecis y guías.'}
            {cambioEstado?.estado === 'POR_LLEGAR' && ' volverá a Por llegar y se quitará el guía asignado.'}
            {cambioEstado?.estado === 'NO_INTERESA' && ' saldrá de la vista pública de Por llegar.'}
          </Typography>
          <TextField
            fullWidth multiline minRows={2}
            label={cambioEstado?.estado === 'POR_LLEGAR' ? 'Notas (opcional)' : 'Motivo'}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCambioEstado(null)} disabled={guardando}>Cancelar</Button>
          <Button
            variant="contained"
            color={cambioEstado?.estado === 'RETIRADA' ? 'error' : 'primary'}
            onClick={confirmarCambioEstado}
            disabled={guardando || (cambioEstado?.estado !== 'POR_LLEGAR' && !notas.trim())}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
